import { useCallback } from "react";
import { useSession } from "users/__session/session-context";
import Proposal from "work/proposals/proposal";
import {
  usePersistedProposalCollaboration,
  type PersistedProposalCollaboration,
} from "work/proposals/_views/_shared/use-persisted-proposal-collaboration";

export type PersistedProposalSaveOrchestration = PersistedProposalCollaboration & {
  /**
   * Whether the current actor may persist proposal-content or reviewer changes for `proposalToSave` —
   * the derivation `handleSaveChanges` on both the draft and active proposal pages used to repeat
   * independently.
   */
  canPersistProposalUpdates: (proposalToSave: Proposal) => boolean;
};

/**
 * Thin orchestration over `usePersistedProposalCollaboration` carrying the piece of the save/submit
 * pipeline both proposal pages re-authored independently: the "can this actor persist updates" gate.
 *
 * It used to carry a second piece — the comment-publish tail every canonical Save ran once the proposal
 * content had been persisted: refresh the Forum rosters, send whatever comment edits and deletions the
 * store was holding, publish the author's drafts if they had chosen to, and toast about it. Each comment
 * action writes as it is performed now, so a Save carries no comments and has nothing to say about them.
 * The tail went with the coupling rather than being left behind as a step that does nothing.
 */
export function usePersistedProposalSaveOrchestration(proposalId?: string): PersistedProposalSaveOrchestration {
  const collaboration = usePersistedProposalCollaboration(proposalId);
  const session = useSession();

  const canPersistProposalUpdates = useCallback(
    (proposalToSave: Proposal): boolean =>
      collaboration.mode === "canonical"
        ? Boolean(collaboration.capabilities?.canEditDraft || collaboration.capabilities?.canManageReviewers)
        : Boolean(session.user?.canShare(proposalToSave)),
    [collaboration.mode, collaboration.capabilities, session],
  );

  return { ...collaboration, canPersistProposalUpdates };
}
